import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Rocket, Plus, Star, Shield, Zap, Trash2, Play, BarChart3,
  LogOut, Crown, Loader2, Sparkles, Palmtree, Heart, Palette, Ban, Trophy, PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import WhatsAppPrompt from "@/components/WhatsAppPrompt";
import PaymentReminder from "@/components/PaymentReminder";
import RenewReminder from "@/components/RenewReminder";
import { ReadingAppCard } from "@/components/InstallReadingApp";
import api, { formatError } from "@/lib/api";
import useLiveGrades from "@/lib/useLiveGrades";
import { shareTrophy } from "@/lib/share";
import HolidayProgress from "@/components/HolidayProgress";
import GiftProgress from "@/components/GiftProgress";
import { useAuth } from "@/context/AuthContext";
import Avatar, { AVATAR_KEYS, AVATAR_MAP } from "@/components/Avatar";
import { toast } from "sonner";
import { ReadingUpsellBanner } from "@/components/ReadingUpsell";
import WelcomeOnboarding from "@/components/intro/WelcomeOnboarding";
import IntroVideoModal from "@/components/intro/IntroVideoModal";


// Reading pilot: invite the parent to the 2-minute survey once their child has used Reading for 14 days.
function ReadingSurveyBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => { api.get("/reading-survey").then(({ data }) => setShow(data.eligible && !data.done)).catch(() => {}); }, []);
  if (!show) return null;
  return (
    <Link to="/reading-survey" data-testid="reading-survey-banner"
      className="mb-6 flex items-center gap-3 rounded-2xl border-2 border-slate-900 bg-green-100 px-5 py-4 font-semibold shadow-[4px_4px_0_0_#0F172A] hover:bg-green-200 transition-colors">
      <img src="/mascot/kiko.png" alt="" className="w-12 h-12" />
      <span className="flex-1">How is Reading with Kiko going? 8 quick questions · Comment se passe la Lecture ? Donnez votre avis (2 min)</span>
      <span className="font-bold">→</span>
    </Link>
  );
}


export default function ParentDashboard() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const liveGrades = useLiveGrades();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", grade: "4", avatar: "fox" });
  const [access, setAccess] = useState(null);
  const [billing, setBilling] = useState(null);
  const [paywallClosed] = useState(false);
  const [video, setVideo] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/children");
      setChildren(data.children);
    } catch (e) {
      toast.error("Could not load your kids.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    refresh();
    api.get("/access").then(({ data }) => setAccess(data)).catch(() => {});
    api.get("/billing").then(({ data }) => setBilling(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daysLeft = (() => {
    if (!access || access.is_admin || user?.plan !== "pilot") return null;
    const end = access.phase2_end;
    if (!end) return null;
    const ms = new Date(end).getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  })();

  const addChild = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/children", {
        name: form.name,
        grade: parseInt(form.grade, 10),
        avatar: form.avatar,
      });
      toast.success(`${form.name} added!`);
      setOpen(false);
      setForm({ name: "", grade: "4", avatar: "fox" });
      load();
      refresh();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeChild = async (id) => {
    try {
      await api.delete(`/children/${id}`);
      toast.success("Profile removed.");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const isPaid = Boolean(billing?.has_access);
  const planName = user?.plan === "pilot" ? "Pilot" : isPaid ? "Active plan" : "Free trial";

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-blue-600 border-2 border-slate-900 flex items-center justify-center shadow-[2px_2px_0_0_#0F172A]">
              <Rocket color="#fff" size={18} />
            </div>
            <span className="font-kids text-lg font-bold hidden sm:inline">Anou Appran</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <span className="hidden sm:block text-sm text-slate-500">{user?.name}</span>
            <Button size="sm" variant="ghost" onClick={() => setVideo(true)} className="rounded-full px-2 sm:px-3" data-testid="rewatch-intro-video"><PlayCircle size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Video tour</span></Button>
            <Button size="sm" variant="ghost" onClick={() => navigate("/parent/invoices")} className="rounded-full px-2 sm:px-3" data-testid="parent-invoices-link"><span className="sm:hidden">Invoices</span><span className="hidden sm:inline">My invoices</span></Button>
            <Button size="sm" variant="ghost" onClick={() => navigate("/rewards")} className="rounded-full px-2 sm:px-3 hidden sm:inline-flex" data-testid="parent-rewards-link">Rewards</Button>
            {user?.is_admin && (
              <Button size="sm" onClick={() => navigate("/admin")} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="nav-admin">Admin</Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="logout-btn">
              <LogOut size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      {billing && !billing.has_access && !billing.pending && !user?.is_admin && !paywallClosed && !open && (
        <WelcomeOnboarding name={user?.name} kid={children[0]} introSeen={Boolean(user?.intro_seen)} onPlans={() => navigate("/pricing")}
          onLogout={() => { logout(); navigate("/login"); }} onAddChild={() => setOpen(true)} onVideo={() => setVideo(true)}
          onDemo={() => (children[0] ? navigate(`/parent/trial/${children[0].id}`) : setOpen(true))} />
      )}
      {(video || (user?.intro_required && children.length > 0)) && (
        <IntroVideoModal mandatory={Boolean(user?.intro_required)} onClose={() => setVideo(false)} />
      )}
      <main className="max-w-6xl mx-auto px-5 py-8">
        <ReadingSurveyBanner />
        <ReadingUpsellBanner billing={billing} />
        <WhatsAppPrompt />
        <PaymentReminder />
        <RenewReminder billing={billing} />
        <ReadingAppCard />
        {user?.survey_prompt && (
          <div className="nb-card bg-white p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="survey-banner">
            <p className="text-sm flex-1">Thanks for being part of the pilot! 1 minute to share your feedback? <span className="text-slate-500">(optional / facultatif)</span></p>
            <button onClick={() => navigate("/survey")} data-testid="survey-banner-open" className="rounded-full bg-blue-600 text-white border-2 border-slate-900 px-4 py-1.5 text-sm font-bold">Give feedback</button>
          </div>
        )}
        {daysLeft != null && daysLeft >= 0 && (
          <div className="flex justify-end mb-4">
            <span data-testid="access-countdown" className={`rounded-full border-2 border-slate-900 px-3 py-1.5 text-sm font-bold ${daysLeft <= 3 ? "bg-red-200 text-red-800" : "bg-white text-slate-800"}`}>
              ⏳ {daysLeft} day{daysLeft === 1 ? "" : "s"} of access left
            </span>
          </div>
        )}
        {billing?.pending && (
          <div className="nb-card bg-sky-50 border-2 border-slate-900 p-4 mb-6 text-sm" data-testid="billing-pending-banner">
            <span className="font-bold">Payment screenshot received.</span> We will confirm it shortly and unlock your subjects.
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-parent text-3xl sm:text-4xl font-bold tracking-tight">Parent dashboard</h1>
            <p className="text-slate-500 mt-1">Manage your learners and track their progress.</p>
          </div>
        </div>

        {/* Plan card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          data-testid="plan-card">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPaid ? "bg-amber-100" : "bg-slate-100"}`}>
              <Crown className={isPaid ? "text-amber-500" : "text-slate-400"} size={24} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Current plan</p>
              <p className="text-lg font-bold">{planName}</p>
              <p className="text-sm text-slate-500">
                {isPaid
                  ? ([...billing.children.filter((c) => c.paid_subjects.length).map((c) => `${c.name}: ${c.paid_subjects.join(", ")} (until ${new Date(c.paid_until).toLocaleDateString()})`),
                    ...billing.children.filter((c) => c.reading_paid?.length && !c.reading_included).map((c) => `${c.name}: Reading kit ${c.reading_paid.map((l) => (l === "en" ? "English" : "Français")).join(" + ")} (until ${new Date(c.reading_paid.map((l) => c.reading_until[l]).sort()[0]).toLocaleDateString()})`)]
                    .join(" · ") || "Maths pilot access")
                  : "10-minute free trial per child with full access · choose a plan to unlock everything"}
              </p>
            </div>
          </div>
          <Link to="/pricing">
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="upgrade-btn">
              <Sparkles size={16} className="mr-1.5" /> {isPaid ? "Renew / change plan" : "Choose a plan"}
            </Button>
          </Link>
        </motion.div>

        {/* Children */}
        <div className="flex items-center justify-between mt-10 mb-4">
          <h2 className="text-xl font-bold">Your learners</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="add-child-btn">
                <Plus size={16} className="mr-1" /> Add child
              </Button>
            </DialogTrigger>
            <DialogContent className="font-parent">
              <DialogHeader>
                <DialogTitle className="font-kids text-xl">Add a learner</DialogTitle>
                <DialogDescription>Create a profile so your child can start learning.</DialogDescription>
              </DialogHeader>
              <form onSubmit={addChild} className="space-y-4 mt-2">
                <div>
                  <Label htmlFor="cname">Child's name</Label>
                  <Input id="cname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required className="mt-1.5 rounded-xl" placeholder="e.g. Alex" data-testid="child-name-input" />
                </div>
                <div>
                  <Label>Grade</Label>
                  <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="child-grade-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6].map((g) => {
                        const soon = !liveGrades.includes(g) && !user?.preview_young;
                        return (
                          <SelectItem key={g} value={String(g)} disabled={soon} data-testid={`grade-option-${g}`}>
                            Grade {g}{soon ? " · Coming soon" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pick an avatar</Label>
                  <div className="grid grid-cols-4 gap-3 mt-2">
                    {AVATAR_KEYS.map((k) => (
                      <button type="button" key={k} onClick={() => setForm({ ...form, avatar: k })}
                        data-testid={`avatar-${k}`}
                        className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all ${form.avatar === k ? "border-blue-600 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}>
                        <Avatar avatar={k} size={44} />
                        <span className="text-xs text-slate-500">{AVATAR_MAP[k].label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700 w-full" data-testid="child-save-btn">
                    {saving ? <Loader2 className="animate-spin" size={18} /> : "Add learner"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
        ) : children.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-children">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 mx-auto flex items-center justify-center mb-4">
              <Plus className="text-blue-600" size={26} />
            </div>
            <p className="font-semibold text-lg">No learners yet</p>
            <p className="text-slate-500 text-sm mt-1">Add your first child to start the 10-minute free trial.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="children-grid">
            {children.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow p-6" data-testid={`child-card-${c.id}`}>
                <div className="flex items-center gap-3">
                  <Avatar avatar={c.avatar} img={c.avatar_img} size={56} />
                  <div>
                    <p className="font-bold text-lg leading-none">{c.name}</p>
                    <p className="text-sm text-slate-500 mt-1">Grade {c.grade}</p>
                  </div>
                  {c.blocked && (
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-300 text-red-600 text-xs font-semibold px-2.5 py-1" data-testid={`child-paused-${c.id}`}>
                      <Ban size={12} /> Paused
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-5">
                  <Stat icon={Star} color="#EAB308" value={c.stars} label="Stars" />
                  <Stat icon={Shield} color="#3B82F6" value={c.shields} label="Shields" />
                  <Stat icon={Zap} color="#10B981" value={c.xp} label="XP" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Stat icon={Heart} color="#EC4899" value={c.effort_points || 0} label="Effort" />
                  <Stat icon={Palette} color="#8B5CF6" value={c.collected_count || 0} label="Avatars" />
                </div>
                {billing && !billing.holiday_booked?.includes(c.id) && (
                  <button onClick={() => navigate(`/pricing?holiday=${c.id}`)} data-testid={`book-holiday-${c.id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 text-white border-2 border-slate-900 px-4 py-2 text-sm font-bold hover:bg-orange-600 transition-colors shadow-[2px_2px_0_0_#0F172A]">
                    <Palmtree size={15} /> Réserver le Holiday Pack · Book the Holiday Pack
                  </button>
                )}
                <HolidayProgress childId={c.id} />
                <GiftProgress childId={c.id} />
                {c.shields > 0 && (
                  <button onClick={() => shareTrophy(c.id).catch(() => toast.error("Could not open sharing"))} data-testid={`share-trophy-${c.id}`}
                    className="mt-3 w-full h-10 rounded-full bg-[#25D366] hover:bg-[#1eb957] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                    <Trophy size={16} /> Share {c.name.split(" ")[0]}'s trophy on WhatsApp
                  </button>
                )}
                <div className="flex gap-2 mt-5">
                  <Button onClick={() => navigate(`/kid/${c.id}`)} disabled={c.blocked} className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-testid={`play-btn-${c.id}`}>
                    <Play size={16} className="mr-1" /> {c.blocked ? "Paused" : "Play"}
                  </Button>
                  <Button variant="outline" onClick={() => navigate(`/parent/child/${c.id}`)} className="rounded-full border-slate-300" data-testid={`progress-btn-${c.id}`}>
                    <BarChart3 size={16} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="rounded-full border-slate-300 text-slate-400 hover:text-red-500" data-testid={`delete-btn-${c.id}`}>
                        <Trash2 size={16} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="font-parent">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove {c.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This deletes their profile and progress. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => removeChild(c.id)} className="bg-red-500 hover:bg-red-600" data-testid={`confirm-delete-${c.id}`}>Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, color, value, label }) {
  return (
    <div className="rounded-xl bg-slate-50 p-2.5 text-center">
      <Icon size={16} className="mx-auto" style={{ color }} />
      <p className="font-bold mt-1 leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{label}</p>
    </div>
  );
}
